import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from 'react-redux';
import { useTranslation } from "react-i18next";
import { cmsOption } from "../services/cms.service";

// Decodes HTML entities (handles single, double, or multiple encoding) so stored icon HTML and text render correctly
const decodeHtmlEntities = (str) => {
  if (!str) return '';
  let curr = String(str);
  let prev = '';
  let iterations = 0;

  while (curr !== prev && iterations < 20 && (curr.includes('&') || curr.includes('%'))) {
    prev = curr;
    curr = curr
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/&#x27;/g, "'")
      .replace(/&#x2F;/g, '/')
      .replace(/&nbsp;/g, ' ');

    try {
      const txt = document.createElement('textarea');
      txt.innerHTML = curr;
      if (txt.value && txt.value !== curr) {
        curr = txt.value;
      }
    } catch (e) {}
    iterations++;
  }

  const trimmed = curr.trim();
  if (trimmed.startsWith('ri-') || trimmed.startsWith('fa-') || trimmed.startsWith('fab ') || trimmed.startsWith('fas ')) {
    curr = `<i class="${trimmed}"></i>`;
  }

  return curr;
};

function Footer() {
  const { t } = useTranslation();
  const [globalData, setGlobalData] = useState({});
  const [cmsPages, setCmsPages] = useState([]);
  const [loadedCms, setLoadedCms] = useState(false);
  const myGlobalData = useSelector((state) => state.globalData);

  useEffect(() => {
    if (myGlobalData && myGlobalData?.data) {
      const settingsData = JSON.parse(myGlobalData?.data);
      setGlobalData(settingsData);
    }
  }, [myGlobalData]);

  useEffect(() => {
    cmsOption({})
      .then((res) => {
        if (res?.success && Array.isArray(res?.data)) {
          setCmsPages(res.data);
        }
        setLoadedCms(true);
      })
      .catch((err) => {
        console.error("Error fetching footer CMS pages:", err);
        setLoadedCms(true);
      });
  }, []);

  const hasAboutUs = !loadedCms || cmsPages.some((page) => page.slug === "about-us");
  const hasPrivacyPolicy = !loadedCms || cmsPages.some((page) => page.slug === "privacy-policy");
  const hasTerms = !loadedCms || cmsPages.some((page) => {
    const slug = (page.slug || "").toLowerCase();
    return slug === "terms-condition" || slug === "terms-and-conditions" || slug.includes("terms");
  });

  const getCmsPageTitle = (page) => {
    const slug = (page.slug || "").toLowerCase();
    const title = (page.title || "").toLowerCase();
    if (slug.includes("terms") || title.includes("terms")) return t("footer.terms_of_use");
    if (slug.includes("privacy") || title.includes("privacy")) return t("footer.privacy_policy");
    if (slug.includes("about") || title.includes("about")) return t("footer.about_us");
    if (slug.includes("tutorial") || title.includes("tutorial")) return t("footer.tutorials");
    if (slug.includes("faq") || title === "faq") return t("footer.faq");
    return page.title;
  };

  // Data structure for the Company links
  const companyLinks = [
    ...(hasAboutUs ? [{ name: t("footer.about_us"), href: "/about-us" }] : []),
    { name: t("footer.faq"), href: "/faq" },
    { name: t("footer.contact_us"), href: "/contact-us" },
  ];

  // Data structure for the Services links
  const servicesLinks = [
    ...(hasPrivacyPolicy ? [{ name: t("footer.privacy_policy"), href: "/privacy-policy" }] : []),
    ...(hasTerms ? [{ name: t("footer.terms_of_use"), href: "/terms-and-conditions" }] : []),
  ];

  const isReservedCmsSlug = (slug = "") => {
    const s = slug.toLowerCase();
    return (
      s === "privacy-policy" ||
      s === "terms-condition" ||
      s === "terms-and-conditions" ||
      s.includes("terms") ||
      s === "about-us"
    );
  };

  return (
    <footer className="bg-zinc-950 text-white m-[10px] lg:m-[15px] rounded-[20px] py-[20px] lg:py-[50px] pb-[20px] lg:pb-[30px]">
      <div className="container">
        {/* Main Footer Content Grid */}
        <div className="grid grid-cols-12 gap-8">
          {/* Section 1: Logo and Text */}
          <div className="col-span-12 md:col-span-4">
            {/* Logo placeholder */}
            <div className="text-xl font-bold mb-4">
              <img src="/images/footer-logo.svg" alt="Footer Logo" />
            </div>
            <p className="text-[14px] text-white max-w-xs !leading-[1.5]">
              {decodeHtmlEntities(globalData?.description)}
            </p>
          </div>

          {/* Section 2: Company Links */}
          <div className="col-span-12 md:col-span-2">
            <h3 className="text-white headings-h4 mb-4">{t("footer.heading_company")}</h3>
            <ul className="space-y-3 m-0 p-0">
              {companyLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-[14px] text-white transition-colors !no-underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Section 3: Services Links */}
          <div className="col-span-12 md:col-span-3">
            <h3 className="headings-h4 mb-4">{t("footer.heading_services")}</h3>
            <ul className="space-y-3 m-0 p-0">
              {servicesLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="text-[14px] text-white transition-colors !no-underline"
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
              {cmsPages
                .filter((page) => !isReservedCmsSlug(page.slug))
                .map((page) => (
                  <li key={page.slug || page._id}>
                    <Link
                      to={`/org-info/${page.slug}`}
                      className="text-[14px] text-white transition-colors !no-underline"
                    >
                      {getCmsPageTitle(page)}
                    </Link>
                  </li>
                ))}
            </ul>
          </div>

          {/* Section 4: Social Media */}
          <div className="col-span-12 md:col-span-3">
            <h3 className="headings-h4 mb-4">{t("footer.heading_social_media")}</h3>
            <ul className="space-y-3 m-0 p-0">
              {globalData?.social_media_details?.map((social) => (
                <li key={social.title}>
                  <a
                    href={social.link}
                    target="blank"
                    className="flex items-center gap-[10px] text-white hover:text-gray-300 transition-colors !no-underline"
                  >
                    <span className="footer-social-media-icon" dangerouslySetInnerHTML={{ __html: decodeHtmlEntities(social.icon) }}></span>
                    <span className="body-lg text-white">{social.title}</span>
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Separator and Copyright */}
        <div className="pt-8 mt-12 border-t border-gray-800 text-center">
          <p className="body-lg text-white">
            {decodeHtmlEntities(globalData?.copy_right_text)}
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
